
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../../utils/axios";

const API_URL = "/software-categories";

const EMPTY_FORM = {
  name: "",
  code: "",
  description: "",
  fields: [],
  is_active: true,
};

const styles = {
  page: {
    padding: "24px",
    background: "#f8fafc",
    minHeight: "100%",
    color: "#172033",
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "24px",
  },
  title: { margin: 0, fontSize: "24px", fontWeight: 700 },
  subtitle: { margin: "6px 0 0", color: "#64748b", fontSize: "14px" },
  button: {
    border: "none",
    borderRadius: "8px",
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
  },
  primary: { background: "#2563eb", color: "#fff" },
  secondary: {
    background: "#fff",
    color: "#334155",
    border: "1px solid #cbd5e1",
  },
  danger: {
    background: "#fff",
    color: "#dc2626",
    border: "1px solid #fecaca",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  stat: {
    background: "#fff",
    padding: "20px",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
  },
  statLabel: { color: "#64748b", fontSize: "13px", marginBottom: "8px" },
  statValue: { fontSize: "26px", fontWeight: 700 },
  panel: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    padding: "18px",
    borderBottom: "1px solid #e2e8f0",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "7px",
    color: "#334155",
  },
  fieldGroup: { marginBottom: "17px" },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    minWidth: "760px",
  },
  th: {
    padding: "13px 16px",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },
  td: {
    padding: "15px 16px",
    fontSize: "14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  },
  badge: {
    display: "inline-block",
    padding: "4px 9px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "16px",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: "14px",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalBody: { padding: "24px" },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 24px",
    borderTop: "1px solid #e2e8f0",
  },
  message: {
    marginBottom: "16px",
    padding: "12px 14px",
    borderRadius: "8px",
    fontSize: "14px",
  },
};

function getErrorMessage(error) {
  const data = error?.response?.data;

  if (data?.errors) {
    return Object.values(data.errors).flat().join(" ");
  }

  return data?.message || error?.message || "Something went wrong.";
}

function normalizeCategory(item) {
  return {
    ...item,
    id: item.id,
    name: item.name || "",
    code: item.code || "",
    description: item.description || "",
    fields: Array.isArray(item.fields) ? item.fields : [],
    is_active:
      item.is_active === true ||
      item.is_active === 1 ||
      item.is_active === "1",
  };
}

export default function SoftwareCategoryTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [customFieldsText, setCustomFieldsText] = useState("");

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(API_URL);
      const responseData = response.data;

      // Supports both { success, data: [...] } and direct array responses.
      const items = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

      setCategories(items.map(normalizeCategory));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!success) return undefined;

    const timeout = setTimeout(() => setSuccess(""), 3500);
    return () => clearTimeout(timeout);
  }, [success]);

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return categories.filter((category) => {
      const matchesSearch =
        !query ||
        category.name.toLowerCase().includes(query) ||
        category.code.toLowerCase().includes(query) ||
        category.description.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && category.is_active) ||
        (statusFilter === "inactive" && !category.is_active);

      return matchesSearch && matchesStatus;
    });
  }, [categories, search, statusFilter]);

  const activeCount = categories.filter((item) => item.is_active).length;
  const inactiveCount = categories.length - activeCount;

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setCustomFieldsText("");
    setFormError("");
    setError("");
    setModalOpen(true);
  };

  const openEditModal = (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name,
      code: category.code,
      description: category.description,
      fields: category.fields,
      is_active: category.is_active,
    });
    setCustomFieldsText(category.fields.join(", "));
    setFormError("");
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setCustomFieldsText("");
    setFormError("");
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setError("");
    setSuccess("");

    const name = form.name.trim();
    const code = form.code.trim();

    if (!name || !code) {
      setFormError("Category name and category code are required.");
      return;
    }

    const fields = [
      ...new Set(
        customFieldsText
          .split(",")
          .map((field) => field.trim())
          .filter(Boolean)
      ),
    ];

    const payload = {
      name,
      code,
      description: form.description.trim() || null,
      fields,
      is_active: Boolean(form.is_active),
    };

    setSaving(true);

    try {
      if (editingId !== null) {
        await axios.put(`${API_URL}/${editingId}`, payload);
        setSuccess("Software category updated successfully.");
      } else {
        await axios.post(API_URL, payload);
        setSuccess("Software category created successfully.");
      }

      setModalOpen(false);
      setEditingId(null);
      setForm({ ...EMPTY_FORM });
      setCustomFieldsText("");

      await fetchCategories();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    const confirmed = window.confirm(
      `Delete software category "${category.name}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeletingId(category.id);
    setError("");
    setSuccess("");

    try {
      await axios.delete(`${API_URL}/${category.id}`);

      setCategories((current) =>
        current.filter((item) => item.id !== category.id)
      );

      setSuccess("Software category deleted successfully.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (category) => {
    setError("");
    setSuccess("");

    try {
      await axios.patch(`${API_URL}/${category.id}`, {
        name: category.name,
        code: category.code,
        description: category.description || null,
        fields: category.fields || [],
        is_active: !category.is_active,
      });

      setCategories((current) =>
        current.map((item) =>
          item.id === category.id
            ? { ...item, is_active: !category.is_active }
            : item
        )
      );

      setSuccess(
        `Software category marked ${!category.is_active ? "active" : "inactive"}.`
      );
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Software Categories</h2>
          <p style={styles.subtitle}>
            Create and manage categories for software assets.
          </p>
        </div>

        <button
          type="button"
          style={{ ...styles.button, ...styles.primary }}
          onClick={openCreateModal}
        >
          + Add Software Category
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            ...styles.message,
            background: "#fef2f2",
            color: "#b91c1c",
            border: "1px solid #fecaca",
          }}
        >
          {error}
          <button
            type="button"
            onClick={fetchCategories}
            style={{
              marginLeft: "12px",
              border: 0,
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {success && (
        <div
          role="status"
          style={{
            ...styles.message,
            background: "#f0fdf4",
            color: "#166534",
            border: "1px solid #bbf7d0",
          }}
        >
          {success}
        </div>
      )}

      <div style={styles.stats}>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Total Categories</div>
          <div style={styles.statValue}>{categories.length}</div>
        </div>

        <div style={styles.stat}>
          <div style={styles.statLabel}>Active Categories</div>
          <div style={{ ...styles.statValue, color: "#15803d" }}>
            {activeCount}
          </div>
        </div>

        <div style={styles.stat}>
          <div style={styles.statLabel}>Inactive Categories</div>
          <div style={{ ...styles.statValue, color: "#b45309" }}>
            {inactiveCount}
          </div>
        </div>
      </div>

      <div style={styles.panel}>
        <div style={styles.toolbar}>
          <input
            type="search"
            aria-label="Search software categories"
            placeholder="Search by name, code, or description..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ ...styles.input, flex: "1 1 250px", maxWidth: "420px" }}
          />

          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            style={{ ...styles.input, width: "180px" }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>

          <button
            type="button"
            style={{ ...styles.button, ...styles.secondary }}
            onClick={fetchCategories}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Custom Fields</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: "center", padding: "36px" }}>
                    Loading software categories...
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: "center", padding: "36px" }}>
                    {categories.length === 0
                      ? "No software categories yet. Click Add Software Category to create one."
                      : "No categories match your search or status filter."}
                  </td>
                </tr>
              ) : (
                filteredCategories.map((category) => (
                  <tr key={category.id}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{category.name}</div>
                      {category.description && (
                        <div
                          style={{
                            color: "#64748b",
                            fontSize: "12px",
                            marginTop: "5px",
                            maxWidth: "260px",
                          }}
                        >
                          {category.description}
                        </div>
                      )}
                    </td>

                    <td style={styles.td}>
                      <code
                        style={{
                          background: "#f1f5f9",
                          padding: "4px 7px",
                          borderRadius: "5px",
                        }}
                      >
                        {category.code}
                      </code>
                    </td>

                    <td style={styles.td}>
                      {category.fields.length
                        ? category.fields.join(", ")
                        : "—"}
                    </td>

                    <td style={styles.td}>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(category)}
                        title="Click to change category status"
                        style={{
                          ...styles.badge,
                          border: "none",
                          cursor: "pointer",
                          background: category.is_active ? "#dcfce7" : "#f1f5f9",
                          color: category.is_active ? "#166534" : "#475569",
                        }}
                      >
                        {category.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>

                    <td style={styles.td}>
                      {category.created_at
                        ? new Date(category.created_at).toLocaleDateString()
                        : "—"}
                    </td>

                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.secondary, padding: "7px 10px" }}
                          onClick={() => openEditModal(category)}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.danger, padding: "7px 10px" }}
                          onClick={() => handleDelete(category)}
                          disabled={deletingId === category.id}
                        >
                          {deletingId === category.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: "14px 18px",
            color: "#64748b",
            fontSize: "13px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          Showing {filteredCategories.length} of {categories.length} software categories
        </div>
      </div>

      {modalOpen && (
        <div
          style={styles.overlay}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="software-category-modal-title"
            style={styles.modal}
          >
            <form onSubmit={handleSubmit}>
              <div style={styles.modalHeader}>
                <h3
                  id="software-category-modal-title"
                  style={{ margin: 0, fontSize: "19px" }}
                >
                  {editingId !== null
                    ? "Edit Software Category"
                    : "Add Software Category"}
                </h3>

                <button
                  type="button"
                  aria-label="Close dialog"
                  onClick={closeModal}
                  disabled={saving}
                  style={{
                    border: 0,
                    background: "transparent",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#64748b",
                  }}
                >
                  ×
                </button>
              </div>

              <div style={styles.modalBody}>
                {formError && (
                  <div
                    role="alert"
                    style={{
                      ...styles.message,
                      background: "#fef2f2",
                      color: "#b91c1c",
                      border: "1px solid #fecaca",
                    }}
                  >
                    {formError}
                  </div>
                )}

                <div style={styles.fieldGroup}>
                  <label htmlFor="software-category-name" style={styles.label}>
                    Category Name *
                  </label>
                  <input
                    id="software-category-name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Operating Systems"
                    maxLength={150}
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label htmlFor="software-category-code" style={styles.label}>
                    Category Code *
                  </label>
                  <input
                    id="software-category-code"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    placeholder="e.g. OPERATING_SYSTEMS"
                    maxLength={100}
                    required
                    pattern="[A-Za-z0-9_-]+"
                    title="Use letters, numbers, hyphens, or underscores only."
                    style={styles.input}
                  />
                  <small style={{ color: "#64748b" }}>
                    Letters, numbers, hyphens and underscores only.
                  </small>
                </div>

                <div style={styles.fieldGroup}>
                  <label htmlFor="software-category-description" style={styles.label}>
                    Description
                  </label>
                  <textarea
                    id="software-category-description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Describe this software category..."
                    rows={3}
                    style={{ ...styles.input, resize: "vertical" }}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label htmlFor="software-category-fields" style={styles.label}>
                    Custom Fields
                  </label>
                  <input
                    id="software-category-fields"
                    value={customFieldsText}
                    onChange={(event) => setCustomFieldsText(event.target.value)}
                    placeholder="License Key, Version, Vendor"
                    style={styles.input}
                  />
                  <small style={{ color: "#64748b" }}>
                    Enter field names separated by commas.
                  </small>
                </div>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                  />
                  Category is active
                </label>
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondary }}
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{ ...styles.button, ...styles.primary }}
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingId !== null
                      ? "Update Category"
                      : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}